
import { AnalyticsConfig, defaultConfig } from './config';
import { EventQueue } from './eventQueue';
import { UserManager } from './userManager';
import { PageTracker } from './pageTracker';
import { PostHogIntegration } from './postHogIntegration';
import { AnalyticsUtils } from './analyticsUtils';
import { AnalyticsEvent, AnalyticsProperties, UserProperties } from './types';

export class AnalyticsService {
  private config: AnalyticsConfig;
  private eventQueue: EventQueue;
  private userManager: UserManager;
  private pageTracker: PageTracker;
  private postHog: PostHogIntegration;

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this.eventQueue = new EventQueue(this.config);
    this.userManager = new UserManager();
    this.pageTracker = new PageTracker();
    this.postHog = new PostHogIntegration();
  }

  setAnalyticsConsent(hasConsent: boolean) {
    this.userManager.setAnalyticsConsent(hasConsent);
    this.postHog.setConsent(hasConsent && this.userManager.getATTConsent());
  }

  setATTConsent(hasConsent: boolean) {
    this.userManager.setATTConsent(hasConsent);
    // Update PostHog consent based on both analytics and ATT consent
    this.postHog.setConsent(this.userManager.getAnalyticsConsent() && hasConsent);
  }

  private shouldTrack(): boolean {
    return this.config.enabled && this.userManager.canTrack();
  }

  async track(event: AnalyticsEvent, properties: AnalyticsProperties = {}) {
    if (!this.shouldTrack()) {
      return;
    }

    const enhancedProperties = AnalyticsUtils.enhanceProperties(properties);

    // Try PostHog first
    if (this.postHog.track(event, enhancedProperties)) {
      return;
    }

    // Fallback to edge function
    const userId = await this.userManager.getCurrentUser();
    if (!userId) {
      console.warn('No user ID available for analytics tracking');
      return;
    }

    this.eventQueue.add({
      operation: 'track',
      distinctId: userId,
      event,
      properties: enhancedProperties,
      timestamp: Date.now(),
    });
  }

  async identify(userProperties: UserProperties) {
    if (!this.shouldTrack()) {
      return;
    }

    // Try PostHog first
    if (this.postHog.identify(undefined, userProperties)) {
      return;
    }

    // Fallback to edge function
    const userId = await this.userManager.getCurrentUser();
    if (!userId) {
      console.warn('No user ID available for analytics identify');
      return;
    }

    this.eventQueue.add({
      operation: 'identify',
      distinctId: userId,
      userProperties,
      timestamp: Date.now(),
    });
  }

  async page(pageName?: string, properties: AnalyticsProperties = {}) {
    if (!this.shouldTrack()) {
      return;
    }

    // Track page leave for previous page if we have one
    if (this.pageTracker.hasActivePageTracking()) {
      await this.pageLeave();
    }

    // Set new page tracking
    this.pageTracker.startPageTracking();

    const enhancedProperties = AnalyticsUtils.enhancePageProperties(pageName, properties);

    // Try PostHog first
    if (this.postHog.page(enhancedProperties)) {
      return;
    }

    // Fallback to edge function
    const userId = await this.userManager.getCurrentUser();
    if (!userId) {
      console.warn('No user ID available for analytics page tracking');
      return;
    }

    this.eventQueue.add({
      operation: 'page',
      distinctId: userId,
      properties: enhancedProperties,
      timestamp: Date.now(),
    });
  }

  async pageLeave() {
    if (!this.shouldTrack() || !this.pageTracker.hasActivePageTracking()) {
      return;
    }

    const duration = this.pageTracker.getPageDuration();
    const pageUrl = this.pageTracker.getCurrentPageUrl();

    if (duration !== null && pageUrl) {
      const properties = {
        $current_url: pageUrl,
        $pathname: new URL(pageUrl).pathname,
        duration: duration,
        timestamp: Date.now(),
      };

      // Try PostHog first
      if (this.postHog.pageLeave(properties)) {
        this.pageTracker.clearPageTracking();
        return;
      }

      // Fallback to edge function
      const userId = await this.userManager.getCurrentUser();
      if (userId) {
        this.eventQueue.add({
          operation: 'pageleave',
          distinctId: userId,
          properties,
          timestamp: Date.now(),
        });
      }
    }

    this.pageTracker.clearPageTracking();
  }

  setUser(userId: string | null) {
    const previousAnonymousId = this.userManager.getAnonymousUserId();
    
    // If user is logging in and we had an anonymous ID, create an alias
    if (userId && previousAnonymousId && userId !== previousAnonymousId) {
      this.createAlias(previousAnonymousId, userId);
    }
    
    this.userManager.setUser(userId);
    this.postHog.setUser(userId);
    
    // Clear anonymous ID when user authenticates
    if (userId) {
      this.userManager.clearAnonymousUserId();
    }
  }

  private async createAlias(anonymousId: string, authenticatedId: string) {
    if (!this.shouldTrack()) return;

    // Try PostHog first
    if (this.postHog.alias(authenticatedId, anonymousId)) {
      return;
    }

    // Fallback to edge function
    this.eventQueue.add({
      operation: 'alias',
      distinctId: authenticatedId,
      alias: anonymousId,
      timestamp: Date.now(),
    });
  }

  // Utility methods for common events
  async trackError(error: Error, context?: string) {
    await AnalyticsUtils.trackError(this.track.bind(this), error, context);
  }

  async trackApiError(endpoint: string, status: number, message: string) {
    await AnalyticsUtils.trackApiError(this.track.bind(this), endpoint, status, message);
  }

  async trackAuthError(operation: string, error: string) {
    await AnalyticsUtils.trackAuthError(this.track.bind(this), operation, error);
  }

  // PostHog-specific methods
  getFeatureFlag(flagKey: string): boolean | string | undefined {
    return this.postHog.getFeatureFlag(flagKey);
  }

  isFeatureEnabled(flagKey: string): boolean {
    return this.postHog.isFeatureEnabled(flagKey);
  }

  startSessionRecording() {
    this.postHog.startSessionRecording();
  }

  stopSessionRecording() {
    this.postHog.stopSessionRecording();
  }

  // Queue management
  getQueueSize(): number {
    return this.eventQueue.getQueueSize();
  }

  async flushEvents() {
    await this.eventQueue.flush();
  }

  dispose() {
    this.eventQueue.clear();
    this.pageTracker.clearPageTracking();
  }
}
